import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { gql, useMutation } from '@apollo/client';

const TEST_AGENT_CONNECTION = gql`
  mutation TestAgentConnection($agentId: String!) {
    testAgentConnection(agentId: $agentId) {
      success
      status
      protocol
      responseTimeMs
      statusCode
      errorMessage
      testedAt
    }
  }
`;

interface ConnectionTestState {
  status: 'idle' | 'success' | 'error';
  message?: string;
}

interface ConnectionTestResult {
  success: boolean;
  status: string;
  protocol: string;
  responseTimeMs?: number;
  statusCode?: number;
  errorMessage?: string;
  testedAt: string;
}

export const useConnectionTest = () => {
  const toast = useToast();
  const [testConnectionMutation, { loading }] = useMutation(TEST_AGENT_CONNECTION);
  const [result, setResult] = useState<ConnectionTestState>({ status: 'idle' });

  const test = useCallback(
    async (agentId: string): Promise<ConnectionTestResult | null> => {
      try {
        const { data } = await testConnectionMutation({
          variables: { agentId },
        });
        const res = data.testAgentConnection as ConnectionTestResult;

        if (res.success) {
          setResult({
            status: 'success',
            message: `Connected in ${res.responseTimeMs}ms`,
          });
          toast({
            title: 'Connection Successful',
            description: `${res.protocol} endpoint is reachable (${res.responseTimeMs}ms)`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          const errorMsg =
            res.status === 'TIMEOUT'
              ? 'Connection timed out'
              : res.errorMessage || 'Unable to reach endpoint';
          setResult({ status: 'error', message: errorMsg });
          toast({
            title: 'Connection Failed',
            description: errorMsg,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
        return res;
      } catch (e: any) {
        const errorMsg = e.message || 'An error occurred';
        setResult({ status: 'error', message: errorMsg });
        toast({
          title: 'Connection Test Error',
          description: errorMsg,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return null;
      }
    },
    [testConnectionMutation, toast]
  );

  const reset = useCallback(() => {
    setResult({ status: 'idle' });
  }, []);

  return { test, loading, result, reset };
};
